import { api } from "./auth";

/**
 * Assignments shape:
 * { [subId]: {
 *     dalnisId: string|null, ketuaId: string|null, anggotaIds: string[],
 *     noSt: string|null, untuk: string|null,
 *     tanggalMulai: string|null, tanggalSelesai: string|null,
 *     jumlahHari: number|null,
 *     subPenugasan: {
 *       id, nama, tanggalMulai, tanggalSelesai, jumlahHari,
 *       noSt: string|null, ketuaId: string|null, anggotaIds: string[],
 *     }[],
 * } }
 *
 * subPenugasan menampung kegiatan tambahan di luar penugasan utama, mis. "Tim
 * melaksanakan FGD dengan BPKP" — jumlahnya bebas, ditambah/dihapus dari UI.
 * noSt/ketuaId/anggotaIds per-entri bersifat opsional — dipakai saat satu
 * sub-kegiatan (mis. Dumas pada Pengawasan dengan Tujuan Tertentu) punya
 * banyak Surat Tugas terpisah, masing-masing dengan tim sendiri.
 */

function emptyAssignment() {
  return {
    dalnisId: null,
    ketuaId: null,
    anggotaIds: [],
    noSt: null,
    untuk: null,
    tanggalMulai: null,
    tanggalSelesai: null,
    jumlahHari: null,
    subPenugasan: [],
  };
}

/**
 * ID unik untuk entri sub-penugasan baru (client-side).
 */
export function uidSubPenugasan() {
  return typeof crypto !== "undefined" && crypto.randomUUID
    ? `sp-${crypto.randomUUID().slice(0, 8)}`
    : `sp-${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 7)}`;
}

/**
 * Jumlah hari penugasan, inklusif tanggal awal dan akhir.
 * Mengembalikan null jika salah satu tanggal kosong/tidak valid.
 */
export function hitungJumlahHari(tanggalMulai, tanggalSelesai) {
  if (!tanggalMulai || !tanggalSelesai) return null;
  const d1 = new Date(tanggalMulai);
  const d2 = new Date(tanggalSelesai);
  if (Number.isNaN(d1.getTime()) || Number.isNaN(d2.getTime())) return null;
  const diff = Math.round((d2 - d1) / 86400000) + 1;
  return diff > 0 ? diff : null;
}

/**
 * Load assignments from the DB.
 * Returns a plain object { [subId]: { dalnisId, ketuaId, anggotaIds } }
 */
export async function loadAssignments() {
  try {
    const res = await api.get("/assignments");
    const data = res.data || {};
    // Normalize: ensure anggotaIds/subPenugasan are always arrays
    Object.keys(data).forEach((k) => {
      if (!Array.isArray(data[k].anggotaIds)) data[k].anggotaIds = [];
      if (!Array.isArray(data[k].subPenugasan)) data[k].subPenugasan = [];
    });
    return data;
  } catch (err) {
    console.warn("assignmentStorage: gagal fetch assignments, fallback ke {}.", err);
    return {};
  }
}

/**
 * Persist a single assignment to the DB.
 */
export async function saveAssignmentAPI(subId, assignment) {
  return api.put(`/assignments/${subId}`, {
    dalnisId: assignment.dalnisId ?? null,
    ketuaId: assignment.ketuaId ?? null,
    anggotaIds: assignment.anggotaIds ?? [],
    noSt: assignment.noSt ?? null,
    untuk: assignment.untuk ?? null,
    tanggalMulai: assignment.tanggalMulai ?? null,
    tanggalSelesai: assignment.tanggalSelesai ?? null,
    subPenugasan: (assignment.subPenugasan ?? [])
      .filter((sp) => sp && sp.nama && sp.nama.trim())
      .map((sp) => ({
        id: sp.id,
        nama: sp.nama.trim(),
        tanggalMulai: sp.tanggalMulai || null,
        tanggalSelesai: sp.tanggalSelesai || null,
        noSt: sp.noSt?.trim() || null,
        ketuaId: sp.ketuaId || null,
        anggotaIds: sp.anggotaIds ?? [],
      })),
  });
}

export function getAssignment(assignments, subId) {
  return assignments[subId] || emptyAssignment();
}

export function setAssignment(assignments, subId, patch) {
  const current = getAssignment(assignments, subId);
  const next = { ...current, ...patch };
  // Dedupe + max 3 anggota
  if (Array.isArray(next.anggotaIds)) {
    next.anggotaIds = Array.from(new Set(next.anggotaIds)).slice(0, 3);
  }
  return { ...assignments, [subId]: next };
}

export function clearAssignment(assignments, subId) {
  const next = { ...assignments };
  delete next[subId];
  return next;
}

// ---------- User-centric derivations ----------

/**
 * Return list of sub-kegiatan assigned to a given user id, across all role slots.
 * Each item is augmented with { role: "DALNIS" | "KETUA_TIM" | "ANGGOTA_TIM" } indicating slot.
 */
export function subsAssignedToUser(assignments, userId, flatSubs) {
  const out = [];
  for (const sub of flatSubs) {
    const a = assignments[sub.id];
    if (!a) continue;
    if (a.dalnisId === userId) out.push({ ...sub, assignmentRole: "DALNIS" });
    else if (a.ketuaId === userId) out.push({ ...sub, assignmentRole: "KETUA_TIM" });
    else if (Array.isArray(a.anggotaIds) && a.anggotaIds.includes(userId))
      out.push({ ...sub, assignmentRole: "ANGGOTA_TIM" });
  }
  return out;
}

/**
 * Workload summary per user.
 * Returns array of { user, total, selesai, onProgress, belumMulai, terhambat }
 */
export function workloadByUser(assignments, users, flatSubs) {
  return users.map((u) => {
    const subs = subsAssignedToUser(assignments, u.id, flatSubs);
    const count = (st) => subs.filter((s) => s.status === st).length;
    return {
      user: u,
      total: subs.length,
      selesai: count("SELESAI"),
      onProgress: count("ON_PROGRESS"),
      belumMulai: count("BELUM_MULAI"),
      terhambat: count("TERHAMBAT"),
    };
  });
}

export function overloadLevel(total) {
  if (total > 7) return "HIGH"; // merah
  if (total > 5) return "MEDIUM"; // kuning
  return "OK";
}
